import React, { useEffect, useMemo } from 'react';
import PageContainer from '../layouts/PageContainer';
import PageHeader from '../components/Nav/PageHeader';
import { numberFormat } from '../util/araknerdlib';
import { useSales } from '../DataStore/SalesContext';
import { useAuth } from '../DataStore/AuthContext';
import SalesHeroKpis from '../components/SalesReport/SalesHeroKpis';
import SalesFilterBar from '../components/SalesReport/SalesFilterBar';
import DailySalesTrendCard from '../components/SalesReport/DailySalesTrendCard';
import BestPlanCard from '../components/SalesReport/BestPlanCard';
import PlanBreakdownCard from '../components/SalesReport/PlanBreakdownCard';
import SalesBreakdownTable from '../components/SalesReport/SalesBreakdownTable';


const SalesReportPage = () => {
  const {
    getSalesBreakdown,
    getSalesSummary,
    transactions,
    metaData,
    filters,
    setFilters,
    setCurrentPage,
    setMounted,
    salesSummary,
    defaultFilters,
  } = useSales();

  const { connectedProfiles } = useAuth();
  const currency = connectedProfiles?.currency || 'UGX';

  const money = (value) =>
    `${currency} ${new Intl.NumberFormat('en-UG', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0)}`;

  const formatCount = (value) => numberFormat(value || 0, { minDigits: 1 });

  useEffect(() => {
    getSalesSummary();
    getSalesBreakdown();
    setMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilter = () => {
    getSalesSummary(true);
    getSalesBreakdown(true);
  };

  const handleReset = () => {
    setFilters({ ...defaultFilters });
    setCurrentPage(1);
    getSalesSummary(true);
    getSalesBreakdown(true);
  };

  const summary =
    salesSummary && salesSummary !== '404' ? salesSummary.summary || {} : {};
  const bestPlan =
    salesSummary && salesSummary !== '404' ? salesSummary.best_plan : null;
  const planBreakdown =
    salesSummary &&
    salesSummary !== '404' &&
    Array.isArray(salesSummary.plans)
      ? salesSummary.plans
      : [];
  const dailyBreakdown =
    salesSummary &&
    salesSummary !== '404' &&
    Array.isArray(salesSummary.daily)
      ? salesSummary.daily
      : [];
  const breakdownRows = Array.isArray(transactions) ? transactions : [];

  const totalAmount = Number(summary.total_amount_sold) || 0;

  const planWithPercent = useMemo(() => {
    return planBreakdown.map((p, idx) => {
      const amount = Number(p.amount_sold) || 0;
      const percent = totalAmount > 0 ? (amount / totalAmount) * 100 : 0;
      return { ...p, __rank: idx + 1, __percent: percent };
    });
  }, [planBreakdown, totalAmount]);

  const maxDaily = useMemo(() => {
    return dailyBreakdown.reduce(
      (m, r) => Math.max(m, Number(r.amount_sold) || 0),
      0
    );
  }, [dailyBreakdown]);

  const setPresetRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);

    setFilters((f) => ({
      ...f,
      start_date: fmt(start),
      end_date: fmt(end),
    }));
  };

  const styles = {
    hero: {
      background:
        'radial-gradient(1200px circle at 0% 0%, rgba(124,58,237,0.25), transparent 55%), radial-gradient(1200px circle at 100% 0%, rgba(16,185,129,0.18), transparent 50%), linear-gradient(135deg, #0b1020, #0f172a)',
      border: '1px solid rgba(255,255,255,0.06)',
      boxShadow: '0 10px 35px rgba(0,0,0,0.45)',
    },
    softCard: {
      background: 'rgba(15, 23, 42, 0.9)',
      border: '1px solid rgba(255,255,255,0.06)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
    },
    kpiTile: (accent) => ({
      background:
        `linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01)), radial-gradient(400px circle at 0% 0%, ${accent}33, transparent 60%)`,
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 14,
      padding: '14px 14px',
      minWidth: 160,
    }),
    stickyFilter: {
      position: 'sticky',
      top: 0,
      zIndex: 20,
      backdropFilter: 'blur(8px)',
      background: 'rgba(2,6,23,0.85)',
      border: '1px solid rgba(255,255,255,0.06)',
    },
    badge: {
      padding: '4px 8px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.2,
    },
  };

  const loadingSummary = !salesSummary;
  const noSummaryData = salesSummary === '404';

  return (
    <PageContainer>
      <PageHeader
        title="Sales Report"
        breadcrumb={
          <li className="breadcrumb-item active" aria-current="page">
            Sales Report
          </li>
        }
        primaryButtons={[
          {
            label: 'Refresh',
            icon: 'fas fa-sync',
            className: 'btn-outline-secondary',
            onClick: () => {
              getSalesSummary(true);
              getSalesBreakdown(true);
            },
          },
        ]}
      />

      <SalesHeroKpis
        summary={summary}
        currency={currency}
      />

      <SalesFilterBar
        filters={filters}
        setFilters={setFilters}
        handleFilter={handleFilter}
        handleReset={handleReset}
        setPresetRange={setPresetRange}
        styles={styles}
      />

      <div className="row row-sm">
        <div className="col-lg-8">
          <DailySalesTrendCard
            dailyBreakdown={dailyBreakdown}
            maxDaily={maxDaily}
            money={money}
            formatCount={formatCount}
            loading={loadingSummary}
            noData={noSummaryData}
            styles={styles}
          />

         
        </div>

        <div className="col-lg-4">
          <BestPlanCard
            bestPlan={bestPlan}
            money={money}
            formatCount={formatCount}
            loading={loadingSummary}
            noData={noSummaryData}
            styles={styles}
          />

        </div>
      </div>
      <div className="row row-sm mt-3">
        <div className="col-12">
            <PlanBreakdownCard
                  plans={planWithPercent}
                  bestPlanId={bestPlan?.plan_id}
                  money={money}
                  formatCount={formatCount}
                  loading={loadingSummary}
                  noData={noSummaryData}
                  styles={styles}
                />
          </div>
      </div>
      

      <div className="row row-sm mt-3">
        <div className="col-12">
          <SalesBreakdownTable
            rows={breakdownRows}
            transactions={transactions}
            metaData={metaData}
            setCurrentPage={setCurrentPage}
            currency={currency}
            formatCount={formatCount}
            styles={styles}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default SalesReportPage;
